import { NavLink } from 'react-router-dom';
import { useYourTurnCount } from '../modules/games';
import { NavIcon } from './icons/NavIcon';
import { getChromeNavEntries } from './navEntries';

/**
 * Mobile bottom-tab nav (DESIGN-BRIEF §3.2). Registry-driven; tolerates any number of
 * entries by shrinking each tab rather than wrapping. Padded for the home-indicator
 * safe area (`viewport-fit=cover` in index.html).
 */
export function BottomNav(): JSX.Element {
  const yourTurn = useYourTurnCount();
  // WS-14: entries opting out via `hideInTabs` (currently just digest — reachable from the
  // wordmark and its push deep-link) don't consume one of the five mobile tab slots.
  const entries = getChromeNavEntries().filter((entry) => !entry.hideInTabs);

  return (
    <nav
      aria-label="Primary"
      className="fixed inset-x-0 bottom-0 z-20 border-t border-line bg-surface/95 backdrop-blur pb-[env(safe-area-inset-bottom)] lg:hidden"
    >
      <ul className="flex">
        {entries.map((entry) => (
          <li key={entry.path} className="min-w-0 flex-1">
            <NavLink
              to={entry.path}
              className={({ isActive }) =>
                `flex min-h-[56px] flex-col items-center justify-center gap-1 px-1 py-2 text-[11px] font-medium ${
                  isActive ? 'text-accent' : 'text-fg-3'
                }`
              }
            >
              {/* Icon set is a per-device preference (Settings → Appearance → Icons); every
                  set renders into the same 24px box so the bar never reflows when it changes. */}
              <span className="relative">
                <NavIcon moduleId={entry.moduleId} emoji={entry.icon} size={24} />
                {entry.moduleId === 'games' && yourTurn > 0 && (
                  <span
                    data-testid="your-turn-badge"
                    className="absolute -right-2 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[10px] font-bold text-inverse-fg"
                  >
                    {yourTurn}
                  </span>
                )}
              </span>
              <span className="truncate">{entry.label}</span>
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
